import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type {
  CreativeAiAnalysis,
  CreativeAiAnalysisDetail,
  CreativeAiAnalysisInput,
  CreativeAiAnalysisListResponse,
  CreativeKind,
} from '../models/analysis.types'
import {
  decodeCreativeBriefCreated,
  decodeCreativeBriefLatestResponse,
  decodeCreativeBriefStatusResponse,
} from '../models/brief.codec'
import type {
  CreativeBriefCreated,
  CreativeBriefLatestResponse,
  CreativeBriefStatusResponse,
} from '../models/brief.types'
import {
  decodeCreativeAiAnalysis,
  decodeCreativeAiAnalysisDetail,
  decodeCreativeAiAnalysisList,
  decodeCreativeLabellingRun,
  decodeLabellingRunList,
  decodeLabelSchemaMeta,
  decodeLabelSchemaResponse,
} from '../models/creativeAi.codec'
import type {
  CreativeLabellingRun,
  LabellingRunListResponse,
  LabellingRunType,
  ManualOverridePolicy,
} from '../models/labelling.types'
import type {
  CreativeLabelSchemaWrite,
  LabelSchemaMeta,
  LabelSchemaResponse,
  LabelSchemaWriteResponse,
  LabelSuggestionRequest,
  LabelSuggestionResponse,
} from '../models/labelSchema.types'

import { ApiError, appHttp, decodeDto, unwrap } from '@/shared/http'

export interface CreativeAiScope {
  accountId: number | string
  workspaceId: number | string
}

export interface CreativeAiSectionQuery {
  type: CreativeKind
  analysis_id?: number
  metric?: string
  platform?: string
  objective?: string
  campaign_type?: string
  date_start?: string
  date_end?: string
}

export interface BenchmarkCreativesApiQuery {
  type: CreativeKind
  analysis_id?: number
  feature_key: string
  feature_value: string
  platform?: string
  objective?: string
  campaign_type?: string
  metric?: string
}

/** A scope key entirely absent leaves that scope untouched server-side; present-but-`{}` deletes
 *  everything in it — the two are not interchangeable, so both stay optional rather than
 *  defaulting to an empty object for an untouched scope (`draftToWriteSchemas`). */
export interface LabelSchemaWriteBody {
  image_schema?: CreativeLabelSchemaWrite
  video_schema?: CreativeLabelSchemaWrite
}

export interface SendBriefBody {
  recipient_email: string
  note?: string
}

export class AnalysisRerunLostError extends Error {
  constructor(readonly originalCause: unknown) {
    super('Analysis was deleted before its replacement request failed')
    this.name = 'AnalysisRerunLostError'
  }
}

function requestConfig(signal?: AbortSignal): AxiosRequestConfig {
  return {
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
    ...(signal ? { signal } : {}),
  }
}

function base(scope: CreativeAiScope): string {
  return `/accounts/${encodeURIComponent(String(scope.accountId))}/workspaces/${encodeURIComponent(
    String(scope.workspaceId),
  )}/metadata/creative-ai`
}

function idPath(id: number | string): string {
  return encodeURIComponent(String(id))
}

async function featureRequest<T>(request: Promise<T>): Promise<T> {
  try {
    return await request
  } catch (cause) {
    throw ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  }
}

function bodyFrom<T>(response: { data: T }): T {
  return unwrap(response)
}

/** The feature's only HTTP boundary; every Django path shares the published metadata base. */
export class CreativeAiApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  async listAnalyses(
    scope: CreativeAiScope,
    signal?: AbortSignal,
  ): Promise<CreativeAiAnalysisListResponse> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/analyses/`, requestConfig(signal)),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeAiAnalysisList)
  }

  async getAnalysis(
    scope: CreativeAiScope,
    analysisId: number | string,
    signal?: AbortSignal,
  ): Promise<CreativeAiAnalysisDetail> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/analyses/${idPath(analysisId)}/`, requestConfig(signal)),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeAiAnalysisDetail)
  }

  async createAnalysis(
    scope: CreativeAiScope,
    input: CreativeAiAnalysisInput,
  ): Promise<CreativeAiAnalysis> {
    const response = await featureRequest(
      this.http.post(`${base(scope)}/analyses/`, input, requestConfig()),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeAiAnalysis)
  }

  async updateAnalysis(
    scope: CreativeAiScope,
    analysisId: number | string,
    input: CreativeAiAnalysisInput | { name: string },
  ): Promise<CreativeAiAnalysis> {
    const response = await featureRequest(
      this.http.patch(`${base(scope)}/analyses/${idPath(analysisId)}/`, input, requestConfig()),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeAiAnalysis)
  }

  async deleteAnalysis(scope: CreativeAiScope, analysisId: number | string): Promise<void> {
    await featureRequest(
      this.http.delete(`${base(scope)}/analyses/${idPath(analysisId)}/`, requestConfig()),
    )
  }

  /** BE-23-03 is not live: preserve the legacy delete-then-create sequence behind one method. */
  async rerunAnalysis(
    scope: CreativeAiScope,
    analysisId: number | string,
    input: CreativeAiAnalysisInput,
  ): Promise<CreativeAiAnalysis> {
    await this.deleteAnalysis(scope, analysisId)
    try {
      return await this.createAnalysis(scope, input)
    } catch (cause) {
      throw new AnalysisRerunLostError(cause)
    }
  }

  /** Legacy parity: restarts the same analysis in place, no draft/config body required. */
  async retryAnalysis(scope: CreativeAiScope, analysisId: number | string): Promise<void> {
    await featureRequest(
      this.http.post(
        `${base(scope)}/analyses/${idPath(analysisId)}/retry/`,
        undefined,
        requestConfig(),
      ),
    )
  }

  async getLabelSchema(scope: CreativeAiScope, signal?: AbortSignal): Promise<LabelSchemaResponse> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/label-schema/`, requestConfig(signal)),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeLabelSchemaResponse)
  }

  async getLabelSchemaMeta(scope: CreativeAiScope, signal?: AbortSignal): Promise<LabelSchemaMeta> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/label-schema/meta/`, requestConfig(signal)),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeLabelSchemaMeta)
  }

  /**
   * Old's `handleSaveChanges` treats `response.status === 202 || response.data.queued` as queued
   * (`CreativeAI.vue`, ORP-20851 follow-up) — a run can freeze machine keys and return 202 with an
   * empty body, no `queued` flag at all. `bodyFrom` only ever exposed `.data`, so the status code
   * needs its own field on the result.
   */
  async updateLabelSchema(
    scope: CreativeAiScope,
    body: LabelSchemaWriteBody,
  ): Promise<{ status: number; data: LabelSchemaWriteResponse }> {
    const response = await featureRequest(
      this.http.patch(`${base(scope)}/label-schema/`, body, requestConfig()),
    )
    return { status: response.status, data: bodyFrom<LabelSchemaWriteResponse>(response) }
  }

  async suggestLabelSchema(
    scope: CreativeAiScope,
    body: LabelSuggestionRequest,
  ): Promise<LabelSuggestionResponse> {
    const response = await featureRequest(
      this.http.post(`${base(scope)}/label-schema/suggest/`, body, requestConfig()),
    )
    return bodyFrom<LabelSuggestionResponse>(response)
  }

  async listLabellingRuns(
    scope: CreativeAiScope,
    options: { limit?: number; signal?: AbortSignal } = {},
  ): Promise<LabellingRunListResponse> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/labelling/runs/`, {
        ...requestConfig(options.signal),
        params: { limit: options.limit ?? 20 },
      }),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeLabellingRunList)
  }

  /**
   * `manualOverridePolicy` is additive (ORP-20456). The field name MUST be exactly
   * `manual_override_policy` — the backend silently ignores any other key (PORT-SPEC §3 tuzak #1),
   * so the "keep my edits" choice would look wired while doing nothing.
   */
  async createLabellingRun(
    scope: CreativeAiScope,
    runType: Extract<LabellingRunType, 'relabel' | 'manual'>,
    manualOverridePolicy?: ManualOverridePolicy,
  ): Promise<CreativeLabellingRun> {
    const body: { run_type: LabellingRunType; manual_override_policy?: ManualOverridePolicy } = {
      run_type: runType,
    }
    if (manualOverridePolicy) body.manual_override_policy = manualOverridePolicy
    const response = await featureRequest(
      this.http.post(`${base(scope)}/labelling/runs/`, body, requestConfig()),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeLabellingRun)
  }

  async getLabellingRun(
    scope: CreativeAiScope,
    runId: number,
    signal?: AbortSignal,
  ): Promise<CreativeLabellingRun> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/labelling/runs/${idPath(runId)}/`, requestConfig(signal)),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeLabellingRun)
  }

  async cancelLabellingRun(scope: CreativeAiScope, runId: number): Promise<CreativeLabellingRun> {
    const response = await featureRequest(
      this.http.post(
        `${base(scope)}/labelling/runs/${idPath(runId)}/cancel/`,
        undefined,
        requestConfig(),
      ),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeLabellingRun)
  }

  async getWhatsWorking(
    scope: CreativeAiScope,
    query: CreativeAiSectionQuery,
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/`, {
        ...requestConfig(signal),
        params: { section: 'whats_working', ...query },
      }),
    )
    return bodyFrom<Record<string, unknown>>(response)
  }

  /**
   * Legacy parity (`getCombinations`): a dedicated endpoint, not a `whats_working` section —
   * carries no date range (the backend derives it from `analysis_id`) and a 404 is an expected,
   * handled outcome (no combination run for this analysis yet), not a transport failure.
   */
  async getCombinations(
    scope: CreativeAiScope,
    query: CreativeAiSectionQuery,
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/combinations/`, { ...requestConfig(signal), params: query }),
    )
    return bodyFrom<Record<string, unknown>>(response)
  }

  /**
   * Legacy parity (`fetchBenchmarkCounts`): counts creatives already carrying a candidate
   * "switch to" value, so Opportunities Analysis can show "{N} Creatives" next to it.
   */
  async getBenchmarkCreatives(
    scope: CreativeAiScope,
    query: BenchmarkCreativesApiQuery,
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/creatives/`, { ...requestConfig(signal), params: query }),
    )
    return bodyFrom<Record<string, unknown>>(response)
  }

  /** Resume shape: an active run in progress, a finished result, or neither. */
  async getLatestBrief(
    scope: CreativeAiScope,
    analysisId: number | string,
    signal?: AbortSignal,
  ): Promise<CreativeBriefLatestResponse> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/briefs/latest/`, {
        ...requestConfig(signal),
        params: { analysis_id: analysisId },
      }),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeBriefLatestResponse)
  }

  /**
   * A 409 `BRIEF_RUN_IN_FLIGHT` is not exceptional — legacy parity reattaches to the active run
   * via `cause.payload.error.details.active_run` rather than treating it as a failure. That read
   * belongs to the caller (module 03 §11: the API stays a thin adapter, conflict interpretation is
   * the flow's business — same split `analysisFlows.ts` already uses for analysis conflicts).
   */
  async createBrief(
    scope: CreativeAiScope,
    analysisId: number | string,
  ): Promise<CreativeBriefCreated> {
    const response = await featureRequest(
      this.http.post(`${base(scope)}/briefs/`, { analysis_id: analysisId }, requestConfig()),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeBriefCreated)
  }

  async getBrief(
    scope: CreativeAiScope,
    executionId: string,
    signal?: AbortSignal,
  ): Promise<CreativeBriefStatusResponse> {
    const response = await featureRequest(
      this.http.get(`${base(scope)}/briefs/${idPath(executionId)}/`, requestConfig(signal)),
    )
    return decodeDto(bodyFrom<unknown>(response), decodeCreativeBriefStatusResponse)
  }

  /** Free — no credit gate, no run lifecycle. */
  async sendBrief(scope: CreativeAiScope, executionId: string, body: SendBriefBody): Promise<void> {
    await featureRequest(
      this.http.post(`${base(scope)}/briefs/${idPath(executionId)}/send/`, body, requestConfig()),
    )
  }
}

export const creativeAiApi = new CreativeAiApi()
